import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseHttpService } from '../../http/base-http.service';
import { environment } from '@environments/environment';
import { ClientConfig } from '@core/models/config/client-config';
import { ClientConfigIdentity } from '@core/models/config/client-config-identity';

/** Maps environment.flavour to the X-App-Id header value. */
const APP_ID_MAP: Record<string, string> = {
    default: 'ss-web',
    shift4: 'wf-web',
};

/**
 * Fetches vendor configuration from the server.
 *
 * Two endpoints (served from the v3 API base — see BaseHttpService default):
 *   GET /client-config          (public)  → vendor tokens (Intercom app_id, etc.)
 *   GET /client-config/identity (authed)  → user-specific secrets (Intercom user_hash)
 *
 * Both include the X-App-Id header so the server resolves brand-specific config.
 */
@Injectable({ providedIn: 'root' })
export class ClientConfigDataService extends BaseHttpService {

    private readonly urls = {
        config: '/client-config',
        identity: '/client-config/identity',
    };

    private readonly xAppIdHeaders = new HttpHeaders({
        'X-App-Id': APP_ID_MAP[environment.flavour] || 'ss-web',
    });

    /** Cached config from the last fetchConfig() call. */
    private config: ClientConfig | null = null;

    /** Resolves when the background fetchConfig() completes (success or failure). */
    private readyResolve!: () => void;
    private readyPromise = new Promise<void>((resolve) => (this.readyResolve = resolve));

    /** Public endpoint — called during app initializer before login. */
    fetchConfig(): Observable<ClientConfig> {
        return this.get(this.urls.config, { headers: this.xAppIdHeaders });
    }

    /** Authenticated endpoint — called when Intercom boots (post session-init). */
    fetchIdentity(): Observable<ClientConfigIdentity> {
        return this.get(this.urls.identity, { headers: this.xAppIdHeaders });
    }

    /** Store config after fetch (called by the app initializer). */
    setConfig(config: ClientConfig | null): void {
        this.config = config;
        this.readyResolve();
    }

    /**
     * Resolves when the initial fetchConfig() completes (success or failure).
     * Use in components or effects that need the config before proceeding.
     */
    whenReady(): Promise<void> {
        return this.readyPromise;
    }

    getIntercomAppId(): string | null {
        return this.config?.intercom?.app_id ?? null;
    }
}
